/**
 * Envoi par morceaux reprenable, côté appareil. Même protocole que `@lakou/sync` (client/televersement.ts, L22) :
 * création idempotente par clé (le serveur rend le décalage déjà acquis), puis morceaux envoyés au décalage exact.
 *
 * Copie volontaire et temporaire : `apps/web` ne déclare pas encore `@lakou/sync` et son tsconfig (lib DOM) refuse de
 * compiler les sources de ce paquet. RFC-L58-001 §3 demande à O1 le branchement ; ce fichier disparaît alors au profit de `televerser`.
 */
export const PREFIXE_TRANSFERT = 'televersement:';
export const TAILLE_MORCEAU_PAR_DEFAUT = 256 * 1024;

/** Stockage de l'appareil (IndexedDB en production, mémoire en test). */
export interface StockageLocal {
  lire<T>(cle: string): Promise<T | undefined>;
  ecrire(cle: string, valeur: unknown): Promise<void>;
  supprimer(cle: string): Promise<void>;
  cles(prefixe?: string): Promise<string[]>;
  vider(): Promise<void>;
}

export interface MetaTransfert {
  readonly cle: string;
  readonly nom: string;
  readonly type: string;
  readonly taille: number;
  readonly empreinteSha256: string;
}

export interface TransportTransfert {
  creer(meta: MetaTransfert): Promise<{ id: string; decalage: number }>;
  decalage(id: string): Promise<number>;
  envoyer(id: string, decalage: number, octets: Uint8Array): Promise<{ decalage: number; etat: 'en_cours' | 'termine' | 'corrompu' }>;
}

export interface OptionsTransfert {
  readonly stockage: StockageLocal;
  readonly transport: TransportTransfert;
  readonly cle: string;
  readonly nom: string;
  readonly type: string;
  readonly octets: Uint8Array;
  readonly tailleMorceau?: number;
  readonly surProgres?: (acquis: number) => void;
}

/**
 * Le serveur a répondu, en panne (5xx) : ce n'est pas une coupure du réseau. L'envoi s'arrête (« echec ») au lieu d'attendre un
 * retour du réseau qui ne changerait rien (S2 du 27/10).
 */
export class RefusDuServeur extends Error {
  constructor(readonly statut: number) {
    super(`Réponse ${statut} du serveur`);
    this.name = 'RefusDuServeur';
  }
}

export interface BilanTransfert {
  readonly etat: 'termine' | 'interrompu' | 'corrompu' | 'echec';
  readonly envoyes: number;
  readonly uploadId: string;
}

export async function empreinteSha256(octets: Uint8Array): Promise<string> {
  const condense = new Uint8Array(await crypto.subtle.digest('SHA-256', new Uint8Array(octets)));
  return Array.from(condense, (o) => o.toString(16).padStart(2, '0')).join('');
}

export async function transferer(options: OptionsTransfert): Promise<BilanTransfert> {
  const { stockage, transport, cle, nom, type, octets, tailleMorceau = TAILLE_MORCEAU_PAR_DEFAUT, surProgres } = options;
  const cleLocale = `${PREFIXE_TRANSFERT}${cle}`;
  const connu = await stockage.lire<{ id: string }>(cleLocale);
  const meta = { cle, nom, type, taille: octets.length, empreinteSha256: await empreinteSha256(octets) };

  let id: string;
  let acquis: number;
  try {
    ({ id, decalage: acquis } = await transport.creer(meta));
  } catch (erreur) {
    if (erreur instanceof RefusDuServeur) {
      await stockage.supprimer(cleLocale);
      return { etat: 'echec', envoyes: 0, uploadId: connu?.id ?? '' };
    }
    return { etat: 'interrompu', envoyes: 0, uploadId: connu?.id ?? '' };
  }
  await stockage.ecrire(cleLocale, { id });

  while (acquis < octets.length) {
    try {
      const reponse = await transport.envoyer(id, acquis, octets.slice(acquis, acquis + tailleMorceau));
      acquis = reponse.decalage;
      surProgres?.(acquis);
      if (reponse.etat === 'corrompu') {
        await stockage.supprimer(cleLocale);
        return { etat: 'corrompu', envoyes: acquis, uploadId: id };
      }
    } catch (erreur) {
      if (erreur instanceof RefusDuServeur) {
        await stockage.supprimer(cleLocale);
        return { etat: 'echec', envoyes: acquis, uploadId: id };
      }
      return { etat: 'interrompu', envoyes: acquis, uploadId: id };
    }
  }
  await stockage.supprimer(cleLocale);
  return { etat: 'termine', envoyes: acquis, uploadId: id };
}
